import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEntityStore, useSchemaStore } from '@ContractBuilder/store';
import { useDrawerStore } from '@ContractBuilder/store/drawer.store';
import { usePbqaEvalUIStore } from '@ContractBuilder/store/pbqa-eval.store';
import { getBlockValidationErrors } from '@helpers/status';
import { useDocumentLocation } from '@hooks/useDocumentLocation';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { AUDIT_LOG_KEY } from '@queries/fetchEntity';
import { useQueryClient } from '@tanstack/react-query';

import { useEvalRunMutations } from '../hooks/useEvalRunMutations';
import { EVAL_BUDGET_KEY, EVAL_RUN_DETAIL_KEY, EVAL_RUNS_LIST_KEY, useEvalRunsData } from '../hooks/useEvalRunsData';
import { describeGuidanceBlock, GUIDANCE_STATUSES } from '../utils/readiness';
import { PbqaEvalView } from '../views/PbqaEval';

/** Duration (ms) of the temporary block highlight when jumping from a finding. */
const GO_TO_BLOCK_HIGHLIGHT_MS = 2000;

/**
 * Controller for the PBQA drawer. Responsibilities:
 *   - hydrate the run list / active run detail / available configs
 *   - own the default "active run" selection (most recent, when nothing is
 *     explicitly picked by the user or the block-pill click handler)
 *   - wire up the start / cancel / grade / resolve / AI-resolution-check
 *     mutations and invalidate caches
 *   - reset drawer-scoped UI state on unmount
 */
export const PbqaEvalController = () => {
  const { submission, setHighlightedBlock } = useEntityStore(({ submission, setHighlightedBlock }) => ({
    submission,
    setHighlightedBlock,
  }));
  const closeDrawer = useDrawerStore(({ closeDrawer }) => closeDrawer);
  // Two preview cohorts share this drawer: `pbqa_eval` is internal staff (who
  // grade findings and start runs), `pbqa_review` is the broker preview. On
  // both lists, the internal view wins.
  const { pbqaEval } = useFeatureAccess();
  // Everyone starts in the broker view. Internal staff can switch on the
  // eval admin tools (run switcher, config picker, grading, duplicates,
  // checklist coverage). Purely visual — API access is unchanged.
  const [isAdminMode, setIsAdminMode] = useState(false);
  const showEvalControls = pbqaEval && isAdminMode;
  const { activeBlockId, activeRunId, setActiveRunId, setActiveBlockId, reset } = usePbqaEvalUIStore();
  const queryClient = useQueryClient();

  const submissionId = submission?.id ?? '';

  const {
    runs,
    runsLoading,
    detail,
    detailLoading,
    detailError,
    configs,
    configsLoading,
    budget,
    refetchRuns,
    refetchDetail,
  } = useEvalRunsData({
    submissionId,
    activeRunId,
    enabled: Boolean(submissionId),
    // Brokers cannot read the config list (403) and do not need it: their
    // run button posts without a config id and the server picks the champion.
    fetchConfigs: pbqaEval,
  });

  // The champion config is the only one a `pbqa_review` broker sees runs of.
  // Their own list arrives already filtered by the server; internal staff
  // fetch everything, so the broker view has to reproduce that filter
  // locally. The admin tools show every run.
  const championConfigId = useMemo(() => configs.find((config) => config.is_champion)?.id, [configs]);

  const visibleRuns = useMemo(() => {
    if (!pbqaEval || isAdminMode) return runs;
    if (!championConfigId) return [];
    return runs.filter((run) => run.eval_config_id === championConfigId);
  }, [championConfigId, isAdminMode, pbqaEval, runs]);
  // Until the configs arrive that filter yields nothing, which would read as
  // "no scan yet" — the intro flashing before the results. Stay on the spinner.
  const visibleRunsLoading = runsLoading || (pbqaEval && !isAdminMode && configsLoading);

  // If no run is explicitly selected, default to the most recent one as soon
  // as the list resolves. A selection that survives in the store but is no
  // longer in the list is also replaced rather than left to 404 the detail
  // fetch: `activeRunId` outlives a drawer close, so a broker holding the old
  // champion's run across a champion swap would otherwise be stuck on a dead
  // selection with nothing in the UI to change it.
  useEffect(() => {
    if (visibleRuns.length === 0) return;
    if (activeRunId && visibleRuns.some((run) => run.id === activeRunId)) return;
    setActiveRunId(visibleRuns[0].id);
  }, [activeRunId, setActiveRunId, visibleRuns]);

  const invalidate = useCallback(() => {
    if (!submissionId) return;
    queryClient.invalidateQueries({ queryKey: EVAL_RUNS_LIST_KEY(submissionId) });
    queryClient.invalidateQueries({ queryKey: EVAL_BUDGET_KEY });
    // Resolving a suggestion is a silent write on the server (no `updated_at` bump), so the Activity
    // panel's audit query — keyed on `updated_at` — has to be told directly.
    queryClient.invalidateQueries({ queryKey: AUDIT_LOG_KEY(submissionId) });
    if (activeRunId) {
      queryClient.invalidateQueries({ queryKey: EVAL_RUN_DETAIL_KEY(submissionId, activeRunId) });
    }
  }, [activeRunId, queryClient, submissionId]);

  // The run detail carries `has_unchecked_changes`, which the server works
  // out by re-serialising the contract — so it goes stale the moment the
  // contract is edited, and nothing else refetches it while the drawer stays
  // open (polling stops once the run is terminal). Edits land in the store
  // as a new `updated_at`; ask for the detail again when it moves, so the
  // re-check button wakes up without a reload. The first render is the
  // mount fetch, not an edit.
  const submissionUpdatedAt = submission?.updated_at;
  const seenUpdatedAt = useRef(submissionUpdatedAt);
  useEffect(() => {
    if (seenUpdatedAt.current === submissionUpdatedAt) return;
    seenUpdatedAt.current = submissionUpdatedAt;
    refetchDetail();
  }, [refetchDetail, submissionUpdatedAt]);

  const {
    onStart,
    onCancel,
    onGrade,
    onResolve,
    onStartResolutionCheck,
    isStarting,
    isCancelling,
    isGrading,
    isResolving,
    isStartingResolutionCheck,
  } = useEvalRunMutations({
    submissionId,
    onStarted: async (runId) => {
      // The list first: the default-selection effect above drops a selection
      // it cannot find in `visibleRuns`, so the new run must be there before
      // it becomes the selection.
      await refetchRuns();
      setActiveRunId(runId);
    },
    onChanged: () => {
      invalidate();
      refetchRuns();
      refetchDetail();
    },
  });

  const handleClose = useCallback(() => {
    setActiveBlockId(undefined);
    closeDrawer();
  }, [closeDrawer, setActiveBlockId]);

  useEffect(() => {
    return () => {
      // Drawer is being torn down — clear activeBlockId so the next open
      // doesn't jump to a stale block, but keep activeRunId so reopening
      // the drawer returns the user to their selected run.
      usePbqaEvalUIStore.setState({ activeBlockId: undefined });
    };
  }, []);

  // When a different submission takes over the drawer (unusual in practice
  // but defended against here), wipe the selection entirely.
  useEffect(() => {
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tied to submission lifecycle
  }, [submissionId]);

  // Between the selection flipping and the detail query catching up, `detail`
  // can still hold a challenger run the admin tools had open — the broker
  // view must not render it for even one frame. Suppressing the loading flag
  // with it keeps the "not reviewed yet" empty state reachable when no
  // champion is configured at all.
  const hidesDetail = Boolean(detail && !visibleRuns.some((run) => run.id === detail.run.id));

  // Guidance has the same bar as finalising the contract: every mandatory
  // field filled, no blank blocks, and a contract still in Draft or Review.
  // The schema store's `isValid` is only as fresh as its last pass, so run
  // one when the drawer mounts — as the status-change modal does.
  const { isFormValid, validate, hasValidator } = useSchemaStore(({ isValid, validate, compiledValidator }) => ({
    isFormValid: isValid,
    validate,
    hasValidator: Boolean(compiledValidator),
  }));
  useEffect(() => {
    if (hasValidator) validate();
  }, [hasValidator, validate]);
  const guidanceBlock = useMemo(
    () =>
      describeGuidanceBlock({
        status: submission?.status,
        isFormValid,
        blockErrors: getBlockValidationErrors(true, submission),
        budget,
      }),
    [budget, isFormValid, submission],
  );

  // Past Review the contract cannot change, so its suggestions cannot be
  // actioned either: the cards stay readable, their buttons go dark.
  const isLocked = Boolean(submission?.status) && !(GUIDANCE_STATUSES as string[]).includes(submission?.status ?? '');
  // "Scroll to Block" needs the block on screen — only the Contract View has it.
  const { isContractView } = useDocumentLocation();

  const availableConfigs = useMemo(
    () => configs.filter((c) => Number.isInteger(c.current_version_number) && (c.current_version_number ?? 0) > 0),
    [configs],
  );

  // Maps for resolving inline `[block:<id>]` / `[section:<id>]` references in
  // finding text to readable, clickable chips.
  const { blockNameById, sectionNameById } = useMemo(() => {
    const blockMap = new Map<string, string>();
    const sectionMap = new Map<string, string>();
    for (const section of submission?.sections ?? []) {
      if (section.id) sectionMap.set(section.id, section.label || section.id);
      for (const block of section.blocks ?? []) {
        if (block.id) blockMap.set(block.id, block.name || 'block');
      }
    }
    return { blockNameById: blockMap, sectionNameById: sectionMap };
  }, [submission]);

  // "Go to block" from a finding card: scroll the contract viewer to the
  // block and briefly flash the highlight — matches the behaviour of the
  // activity log. Intentionally does not touch the PBQA drawer's
  // `activeBlockId`, which is reserved for the reverse flow (clicking a
  // block's finding-count pill to open the drawer scrolled to that block).
  const handleGoToBlock = useCallback(
    (blockId: string | undefined) => {
      if (!blockId || !isContractView) return;
      setHighlightedBlock(blockId, true);
      window.setTimeout(() => {
        // Only clear if no one else has since taken over the highlight.
        const current = useEntityStore.getState().highlightedBlockId;
        if (current === blockId) {
          setHighlightedBlock(undefined);
        }
      }, GO_TO_BLOCK_HIGHLIGHT_MS);
    },
    [isContractView, setHighlightedBlock],
  );

  if (!submission) {
    return null;
  }

  return (
    <PbqaEvalView
      runs={visibleRuns}
      runsLoading={visibleRunsLoading}
      detail={hidesDetail ? undefined : detail}
      detailLoading={hidesDetail ? false : detailLoading}
      detailError={Boolean(detailError)}
      onRetryDetail={() => refetchDetail()}
      configs={availableConfigs}
      championConfigId={championConfigId}
      configsLoading={configsLoading}
      budget={budget}
      guidanceBlock={guidanceBlock}
      isLocked={isLocked}
      canFocusBlock={isContractView}
      activeRunId={activeRunId}
      activeBlockId={activeBlockId}
      isStarting={isStarting}
      isCancelling={isCancelling}
      isGrading={isGrading}
      isResolving={isResolving}
      isStartingResolutionCheck={isStartingResolutionCheck}
      showEvalControls={showEvalControls}
      canToggleAdminMode={pbqaEval}
      isAdminMode={showEvalControls}
      onToggleAdminMode={() => setIsAdminMode((v) => !v)}
      onClose={handleClose}
      onSelectRun={setActiveRunId}
      onStart={onStart}
      onCancel={onCancel}
      onStartResolutionCheck={onStartResolutionCheck}
      onGrade={onGrade}
      onResolve={onResolve}
      onFocusBlock={handleGoToBlock}
      blockNameById={blockNameById}
      sectionNameById={sectionNameById}
    />
  );
};
